/**
 * Tech Rationalization Widget (issue #1654, part of epic #1650)
 *
 * Shows consolidation opportunities (multiple technologies per category,
 * off-strategy technologies still in use, low-adoption technologies),
 * matching `homepage-dashboard-spec.md`'s "Technology Rationalization
 * Opportunities" widget. Administrator only — see
 * `backend/src/graphql/resolvers/techRationalization.ts`. Follows the
 * single-query + `useStatisticsWidget` pattern shared by
 * `DependencyHealthWidget`.
 */

import { useTechRationalization, type RationalizationOpportunity } from '../../graphql/queries';
import { DashboardWidget } from './DashboardWidget';
import { useStatisticsWidget } from './useStatisticsWidget';
import type { WidgetConfig } from './types';
import '@tylertech/forge/icon';
import './TechRationalizationWidget.css';

const WIDGET_CONFIG: WidgetConfig = {
  id: 'tech-rationalization',
  title: 'Tech Rationalization',
  size: 'medium',
  refreshInterval: 600000, // 10 minutes — matches the sibling widgets' cadence
  showRefresh: true,
  showExpand: false,
};

interface TechRationalizationStats {
  opportunities: RationalizationOpportunity[];
}

type TechRationalizationQueryData = ReturnType<typeof useTechRationalization>['data'];

function extractTechRationalizationStats(
  data: TechRationalizationQueryData
): TechRationalizationStats | null {
  if (!data?.techRationalization) {
    return null;
  }
  return data.techRationalization;
}

function opportunityTechnologies(opportunity: RationalizationOpportunity): string {
  if (opportunity.technologies && opportunity.technologies.length > 0) {
    return opportunity.technologies.join(', ');
  }
  return opportunity.technology ?? '';
}

export function TechRationalizationWidget(): JSX.Element {
  const { stats, widgetState, handleRefresh } = useStatisticsWidget(
    useTechRationalization(),
    extractTechRationalizationStats,
    WIDGET_CONFIG.refreshInterval!
  );

  const IconElement = 'forge-icon' as React.ElementType;

  if (!stats || stats.opportunities.length === 0) {
    return (
      <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
        <div className="tech-rationalization-widget__empty">
          <IconElement name="check_circle" className="tech-rationalization-widget__empty-icon" />
          <p className="forge-typography--body2">No rationalization opportunities found</p>
        </div>
      </DashboardWidget>
    );
  }

  return (
    <DashboardWidget config={WIDGET_CONFIG} state={widgetState} onRefresh={handleRefresh}>
      <ul className="tech-rationalization-widget__list" data-testid="tech-rationalization-list">
        {stats.opportunities.map((opportunity, index) => (
          <li
            key={`${opportunity.category}-${opportunity.issue}-${index}`}
            className="tech-rationalization-widget__row"
            data-testid="tech-rationalization-row"
          >
            <div className="tech-rationalization-widget__row-header">
              <IconElement
                name="lightbulb"
                className="tech-rationalization-widget__icon"
                aria-hidden="true"
              />
              <span className="tech-rationalization-widget__issue">{opportunity.issue}</span>
            </div>
            <p className="tech-rationalization-widget__technologies forge-typography--body2">
              {opportunityTechnologies(opportunity)}
            </p>
            <p className="tech-rationalization-widget__recommendation forge-typography--caption">
              {opportunity.recommendation}
            </p>
          </li>
        ))}
      </ul>
    </DashboardWidget>
  );
}
